import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  Clock, Calendar, Users, BarChart3, Shield, Smartphone,
  ChevronDown, Menu, X, LogIn, User, Lock, ArrowRight, ArrowLeft,
  MapPin, PlayCircle, CheckCircle2, Coffee, Plane,
  Mail, Phone, Sparkles, FileCheck, UserCheck,
  Building2, Send, HelpCircle, MessageSquare,
} from 'lucide-react';
import Logo from '../components/Logo';
import './HomePage.css';

/* ─── Stock images (Unsplash) ─── */
const IMAGES = {
  hero: 'https://images.unsplash.com/photo-1552664730-d307ca884978?w=1400&q=85',
  fichaje: 'https://images.unsplash.com/photo-1506784983877-45594efa4cbe?w=800&q=80',
  calendario: 'https://images.unsplash.com/photo-1506784365847-bbad939e9335?w=800&q=80',
  vacaciones: 'https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=800&q=80',
  equipo: 'https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=800&q=80',
  reportes: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&q=80',
  movilidad: 'https://images.unsplash.com/photo-1512428559087-560fa5ceab42?w=800&q=80',
  team: 'https://images.unsplash.com/photo-1600880292203-757bb62b4baf?w=1200&q=80',
  login: 'https://images.unsplash.com/photo-1711630031169-ad4455d15b99?q=80&w=1209&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D',
  access: 'https://images.unsplash.com/photo-1600880292203-757bb62b4baf?w=1200&q=85',
  proceso: 'https://images.unsplash.com/photo-1773332611612-ffdaa753afb1?q=80&w=687&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDF8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D',
  faq: 'https://images.unsplash.com/photo-1594928274321-4497d98e4f48?q=80&w=1332&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D',
};

/* ─── Intersection Observer Hook ─── */
const useIntersectionObserver = () => {
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add('animate-in');
        });
      },
      { threshold: 0.12, rootMargin: '0px 0px -40px 0px' }
    );
    const elements = document.querySelectorAll('.observe-me');
    elements.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);
};

/* ─── Demo Calendar Mock ─── */
const CalendarMock = () => {
  const days = ['L', 'M', 'X', 'J', 'V', 'S', 'D'];
  // 35 celdas (5 semanas). Demo.
  const cells = Array.from({ length: 35 }, (_, i) => {
    const dayNum = i - 2; // empieza en miércoles
    if (dayNum < 1 || dayNum > 30) return { empty: true };
    let type = 'normal';
    if ([6, 7, 13, 14, 20, 21, 27, 28].includes(dayNum)) type = 'weekend';
    if ([15, 16, 17, 18].includes(dayNum)) type = 'vacation';
    if ([1, 2, 3, 5, 8, 9, 10, 11, 12, 22, 23, 24, 25, 26, 29, 30].includes(dayNum)) type = 'worked';
    if (dayNum === 14) type = 'today';
    return { day: dayNum, type };
  });

  return (
    <div className="calendar-mock">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="text-xs font-semibold text-ink-muted uppercase tracking-wider">Abril</div>
          <div className="text-lg font-bold text-ink font-display">2026</div>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1.5 text-xs text-ink-muted">
            <span className="w-2.5 h-2.5 rounded-sm bg-primary-light"></span>
            Trabajado
          </div>
          <div className="flex items-center gap-1.5 text-xs text-ink-muted">
            <span className="w-2.5 h-2.5 rounded-sm bg-accent-light"></span>
            Vacaciones
          </div>
        </div>
      </div>
      <div className="calendar-grid">
        {days.map((d) => (
          <div key={d} className="calendar-day header">{d}</div>
        ))}
        {cells.map((c, i) => {
          if (c.empty) return <div key={i} className="calendar-day" style={{ background: 'transparent' }}></div>;
          const cls = ['calendar-day'];
          if (c.type === 'vacation') cls.push('vacation');
          if (c.type === 'worked') cls.push('worked');
          if (c.type === 'today') cls.push('today');
          return <div key={i} className={cls.join(' ')}>{c.day}</div>;
        })}
      </div>
    </div>
  );
};

/* ─── Main Component ─── */
const HomePage = () => {
  const { login } = useAuth();
  const navigate = useNavigate();

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [navScrolled, setNavScrolled] = useState(false);
  const [activeStep, setActiveStep] = useState(0);

  const [loginUsername, setLoginUsername] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);

  const [openFaq, setOpenFaq] = useState(null);

  /* Form mode inside the login card: 'login' or 'access' */
  const [formMode, setFormMode] = useState('login');

  const openAccessForm = useCallback(() => {
    setMobileMenuOpen(false);
    setFormMode('access');
    const el = document.getElementById('login');
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  /* Access request form (lives inside the login card now) */
  const [accessForm, setAccessForm] = useState({ empresa: '', rango: '', email: '' });
  const [accessErrors, setAccessErrors] = useState({});
  const [accessSent, setAccessSent] = useState(false);
  const [accessLoading, setAccessLoading] = useState(false);

  /* FAQ right-column mode: 'faq' (accordion) or 'question' (question form) */
  const [faqMode, setFaqMode] = useState('faq');
  const [questionForm, setQuestionForm] = useState({ empresa: '', cliente: '', pregunta: '' });
  const [questionErrors, setQuestionErrors] = useState({});
  const [questionSent, setQuestionSent] = useState(false);
  const [questionLoading, setQuestionLoading] = useState(false);

  const handleQuestionSubmit = async (e) => {
    e.preventDefault();
    const errors = {};
    if (!questionForm.empresa.trim()) errors.empresa = 'Indica tu empresa';
    if (!questionForm.cliente) errors.cliente = 'Selecciona una opción';
    if (!questionForm.pregunta.trim()) errors.pregunta = 'Escribe tu pregunta';
    if (Object.keys(errors).length > 0) { setQuestionErrors(errors); return; }
    setQuestionErrors({});
    setQuestionLoading(true);
    await new Promise((r) => setTimeout(r, 700));
    setQuestionLoading(false);
    setQuestionSent(true);
    setQuestionForm({ empresa: '', cliente: '', pregunta: '' });
  };

  const handleAccessSubmit = async (e) => {
    e.preventDefault();
    const errors = {};
    if (!accessForm.empresa.trim()) errors.empresa = 'Indica el nombre de la empresa';
    if (!accessForm.rango) errors.rango = 'Selecciona un rango';
    if (!accessForm.email.trim()) errors.email = 'Indica un email';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(accessForm.email)) errors.email = 'Email no válido';
    if (Object.keys(errors).length > 0) { setAccessErrors(errors); return; }
    setAccessErrors({});
    setAccessLoading(true);
    // Demo: sin backend — simulamos envío.
    await new Promise((r) => setTimeout(r, 700));
    setAccessLoading(false);
    setAccessSent(true);
    setAccessForm({ empresa: '', rango: '', email: '' });
  };

  useIntersectionObserver();

  useEffect(() => {
    const onScroll = () => setNavScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveStep((prev) => (prev + 1) % 4);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  const scrollTo = useCallback((id) => {
    setMobileMenuOpen(false);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    setLoginLoading(true);
    try {
      await login(loginUsername, loginPassword);
      navigate('/panel', { replace: true });
    } catch (err) {
      setLoginError(err.message || 'No se pudo iniciar sesión.');
    } finally {
      setLoginLoading(false);
    }
  };

  /* Data */
  const navLinks = [
    { label: 'Inicio', target: 'hero' },
    { label: 'Cómo funciona', target: 'proceso' },
    { label: 'Funciones', target: 'funciones' },
    { label: 'Calendario', target: 'calendario' },
    { label: 'Acceso', target: 'login' },
    { label: 'FAQ', target: 'faq' },
  ];

  const processSteps = [
    { icon: Smartphone, title: 'Abre TemControl', desc: 'Web o móvil, con tu usuario corporativo.' },
    { icon: PlayCircle, title: 'Ficha entrada', desc: 'Un toque y queda registrado con hora y ubicación.' },
    { icon: Coffee, title: 'Pausas y tareas', desc: 'Registra descansos, turnos y horas extra.' },
    { icon: FileCheck, title: 'Cierra jornada', desc: 'Informe automático al final del día.' },
  ];

  const features = [
    {
      icon: Clock,
      tag: 'Fichaje',
      title: 'Entrada y salida en un toque',
      desc: 'Registro horario inteligente con geolocalización opcional, detección de red y validación anti-fraude.',
      image: IMAGES.fichaje,
    },
    {
      icon: Calendar,
      tag: 'Calendarios',
      title: 'Calendario de equipo unificado',
      desc: 'Visualiza turnos, eventos, festivos y ausencias de todo el equipo en un solo calendario compartido.',
      image: IMAGES.calendario,
    },
    {
      icon: Plane,
      tag: 'Ausencias',
      title: 'Vacaciones y permisos',
      desc: 'Solicita días, aprueba con un clic y controla el saldo anual de cada empleado automáticamente.',
      image: IMAGES.vacaciones,
    },
    {
      icon: Users,
      tag: 'Equipo',
      title: 'Gestión de personas',
      desc: 'Organiza departamentos, responsables y jerarquías. Cada empleado tiene su propio panel y rol.',
      image: IMAGES.equipo,
    },
    {
      icon: BarChart3,
      tag: 'Reportes',
      title: 'Informes en tiempo real',
      desc: 'Horas trabajadas, absentismo, horas extra, productividad por equipo. Exportable a Excel y PDF.',
      image: IMAGES.reportes,
    },
    {
      icon: Smartphone,
      tag: 'Movilidad',
      title: 'App iOS y Android nativa',
      desc: 'Ficha desde cualquier parte. Sincronización inmediata con la web y notificaciones push.',
      image: IMAGES.movilidad,
    },
  ];

  const benefits = [
    'Cumplimiento del RD-Ley 8/2019 de registro horario',
    'Firmado digitalmente y almacenamiento 4 años',
    'Notificaciones automáticas de horas extra',
    'Exportación a gestorías y sistemas de nómina',
    'Panel de administración con permisos granulares',
    'Soporte técnico en español, 48h máximo',
  ];

  const faqs = [
    {
      q: '¿Qué es TemControl?',
      a: 'Una plataforma pensada para simplificar la gestión horaria de tu empresa: fichajes, vacaciones, calendarios y reportes en un solo lugar, accesible desde web y móvil.'
    },
    {
      q: '¿Para qué tipo de empresas está pensado?',
      a: 'Para cualquier empresa que necesite llevar un control horario claro de su equipo, sin importar el tamaño ni el sector. Nos adaptamos a tus procesos, no al revés.'
    },
    {
      q: '¿Puedo probar la plataforma antes de contratarla?',
      a: 'Sí. Podrás acceder a una demo guiada con nuestro equipo para que veas cómo se adapta a tu empresa antes de tomar cualquier decisión.'
    },
    {
      q: '¿Cómo empiezo a usar TemControl?',
      a: 'Solicita acceso desde esta misma web indicando unos datos básicos. Nuestro equipo contactará contigo para acompañarte durante todo el proceso de alta.'
    },
    {
      q: '¿Está disponible también en el móvil?',
      a: 'Sí. TemControl funciona en web, Android y iOS con una experiencia consistente y sincronización en tiempo real entre dispositivos.'
    },
  ];

  return (
    <div className="min-h-screen bg-background text-ink">
      {/* ======= NAVBAR ======= */}
      <nav className={`navbar ${navScrolled ? 'scrolled' : 'transparent'}`}>
        <div className="max-w-7xl mx-auto px-4 md:px-6 flex items-center justify-between">
          <button
            onClick={() => scrollTo('hero')}
            className="flex items-center gap-2.5 group"
          >
            <Logo size={38} variant="onLight" />
            <span className="font-display font-bold text-xl text-ink">
              TemControl
            </span>
          </button>

          <div className="hidden lg:flex items-center gap-7">
            {navLinks.map((l) => (
              <button key={l.target} onClick={() => scrollTo(l.target)} className="nav-link">
                {l.label}
              </button>
            ))}
          </div>

          <div className="hidden lg:flex items-center gap-3">
            <button
              onClick={() => scrollTo('login')}
              className="flex items-center gap-2 px-4 py-2 rounded-xl font-semibold text-sm text-primary-darker hover:bg-primary-soft transition-colors"
            >
              <LogIn size={16} /> Acceder
            </button>
            <button
              onClick={openAccessForm}
              className="flex items-center gap-2 px-4 py-2 rounded-xl font-semibold text-sm bg-primary text-white hover:bg-primary-dark transition-all"
            >
              Solicitar acceso <ArrowRight size={16} />
            </button>
          </div>

          <button
            className="lg:hidden p-2 rounded-lg"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Menú"
          >
            {mobileMenuOpen
              ? <X size={22} className="text-ink" />
              : <Menu size={22} className="text-ink" />
            }
          </button>
        </div>

        <div className={`mobile-menu lg:hidden ${mobileMenuOpen ? 'open' : ''}`}>
          <div className="bg-white border-t border-slate-200 px-6 py-4 flex flex-col gap-1">
            {navLinks.map((l) => (
              <button
                key={l.target}
                onClick={() => scrollTo(l.target)}
                className="text-left py-2.5 text-ink-muted hover:text-primary-dark font-medium"
              >
                {l.label}
              </button>
            ))}
            <button
              onClick={openAccessForm}
              className="mt-3 bg-primary text-white px-4 py-3 rounded-xl font-semibold text-sm"
            >
              Solicitar acceso
            </button>
          </div>
        </div>
      </nav>

      {/* ======= HERO ======= */}
      <section id="hero" className="hero-section">
        <div className="max-w-7xl mx-auto px-4 md:px-6 w-full py-24">
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
            <div className="text-ink">
              <span className="eyebrow">Gestión horaria</span>
              <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold leading-[1.05] mb-6 text-balance">
                La Gestión Horaria de las empresas <span className="text-accent">modernas</span>.
              </h1>

              <p className="text-lg text-ink-muted leading-relaxed mb-8 max-w-xl text-balance">
                Fichajes, vacaciones, calendarios y reportes legales en una sola plataforma.
                Diseñada para equipos que valoran su tiempo.
              </p>

              <div className="flex flex-wrap gap-3 mb-8">
                <button onClick={openAccessForm} className="btn-primary">
                  Solicitar acceso <ArrowRight size={18} />
                </button>
                <button onClick={() => scrollTo('proceso')} className="btn-ghost">
                  <PlayCircle size={18} /> Ver cómo funciona
                </button>
              </div>

              <div className="flex flex-wrap items-center gap-x-6 gap-y-3 text-sm text-ink-muted">
                <div className="flex items-center gap-2">
                  <CheckCircle2 size={16} className="text-accent-dark" /> Cumple normativa laboral
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 size={16} className="text-accent-dark" /> Web + iOS + Android
                </div>
              </div>
            </div>

            <div className="hero-image-wrap">
              <img src={IMAGES.hero} alt="Equipo trabajando con TemControl" className="hero-image" loading="eager" />
              <div className="hero-image-accent hero-image-accent--pink" aria-hidden="true" />
              <div className="hero-image-accent hero-image-accent--sky" aria-hidden="true" />
            </div>
          </div>
        </div>
      </section>

      {/* ======= PROCESO ======= */}
      <section id="proceso" className="section-full bg-white">
        <div className="max-w-7xl mx-auto w-full">
          <div className="text-center mb-16 max-w-3xl mx-auto">
            <div className="observe-me">
              <span className="eyebrow">Cómo funciona</span>
            </div>
            <h2 className="observe-me text-3xl md:text-5xl font-bold mb-5 text-balance">
              Cuatro pasos y estás dentro.
            </h2>
            <p className="observe-me text-lg text-ink-muted text-balance">
              Sin formularios eternos, sin configuraciones complejas. Abrir, fichar y listo.
            </p>
          </div>

          <div className="grid lg:grid-cols-5 gap-12 lg:gap-16 items-center">
            {/* LEFT: product visual */}
            <div className="lg:col-span-2 proceso-visual observe-me slide-left">
              <img src={IMAGES.proceso} alt="" loading="lazy" />
              <div className="proceso-visual-badge">
                <div className="proceso-visual-badge-dot"></div>
                <span>Jornada activa</span>
              </div>
            </div>

            {/* RIGHT: vertical steps */}
            <div className="lg:col-span-3 proceso-steps-v">
              {processSteps.map((step, idx) => {
                const Icon = step.icon;
                return (
                  <div
                    key={idx}
                    className={`proceso-step-v ${activeStep === idx ? 'active' : ''}`}
                    onMouseEnter={() => setActiveStep(idx)}
                  >
                    <div className="proceso-step-v-number">
                      <Icon size={22} strokeWidth={2} />
                      <span className="proceso-step-v-count">{idx + 1}</span>
                    </div>
                    <div className="proceso-step-v-content">
                      <h3>{step.title}</h3>
                      <p>{step.desc}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* ======= FUNCIONES ======= */}
      <section id="funciones" className="section-full funciones-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16 max-w-3xl mx-auto">
            <div className="observe-me"><span className="eyebrow">Funciones</span></div>
            <h2 className="observe-me text-3xl md:text-5xl font-bold mb-5 text-balance">
              Todo lo que necesita una empresa moderna.
            </h2>
            <p className="observe-me text-lg text-ink-muted text-balance">
              Desde el primer fichaje hasta el informe trimestral a la gestoría.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 stagger-children">
            {features.map((f, i) => {
              const Icon = f.icon;
              return (
                <div key={i} className="feature-card observe-me">
                  <div className="feature-card-image">
                    <img src={f.image} alt={f.title} loading="lazy" />
                    <span className="feature-card-tag">{f.tag}</span>
                    <div className="feature-card-icon-overlay">
                      <Icon size={22} strokeWidth={2} />
                    </div>
                  </div>
                  <div className="feature-card-body">
                    <h3>{f.title}</h3>
                    <p>{f.desc}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ======= CALENDARIO + BENEFICIOS ======= */}
      <section id="calendario" className="section-full split-section">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="observe-me slide-left">
              <span className="eyebrow">Calendario inteligente</span>
              <h2 className="text-3xl md:text-4xl font-bold mb-5 text-balance">
                Todo tu equipo, en una sola vista.
              </h2>
              <p className="text-lg text-ink-muted mb-8 leading-relaxed">
                Cada persona ve su calendario personal con fichajes y ausencias.
                Los responsables ven su equipo completo con una vista agrupada.
              </p>

              <ul className="space-y-3">
                {benefits.map((b, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-primary-light flex items-center justify-center flex-shrink-0 mt-0.5">
                      <CheckCircle2 size={14} className="text-primary-dark" />
                    </div>
                    <span className="text-ink text-[0.95rem]">{b}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="observe-me slide-right">
              <CalendarMock />
            </div>
          </div>
        </div>
      </section>

      {/* ======= LOGIN + SOLICITAR ACCESO (merged) ======= */}
      <section id="login" className="section-full login-section">
        <div className="max-w-6xl mx-auto w-full">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* LEFT: text + visual */}
            <div className="observe-me slide-left">
              <span className="eyebrow">
                {formMode === 'login' ? 'Acceso seguro' : 'Para empresas'}
              </span>
              <h2 className="text-3xl md:text-4xl font-bold mb-5 text-balance">
                {formMode === 'login'
                  ? 'Entra y ficha en segundos.'
                  : 'Empieza con TemControl hoy mismo.'}
              </h2>
              <p className="text-lg text-ink-muted mb-6 leading-relaxed text-balance">
                {formMode === 'login'
                  ? 'Accede con tus credenciales para consultar tus fichajes, solicitar vacaciones y gestionar tu jornada.'
                  : 'Cuéntanos sobre tu empresa. Nuestro equipo revisa la solicitud y te contacta en menos de 48h.'}
              </p>
              <div className="flex flex-wrap gap-4 text-sm text-ink-muted mb-8">
                <div className="flex items-center gap-2">
                  <Shield size={16} className="text-primary-dark" />
                  {formMode === 'login' ? 'Cifrado extremo a extremo' : 'Migración desde tu sistema'}
                </div>
                <div className="flex items-center gap-2">
                  <UserCheck size={16} className="text-primary-dark" />
                  {formMode === 'login' ? 'Doble factor disponible' : 'Formación para tu equipo'}
                </div>
              </div>

              <div className="login-visual">
                <img
                  src={IMAGES.login}
                  alt=""
                  loading="lazy"
                  className={`login-visual-img ${formMode === 'login' ? 'visible' : ''}`}
                />
                <img
                  src={IMAGES.access}
                  alt=""
                  loading="lazy"
                  className={`login-visual-img ${formMode === 'access' ? 'visible' : ''}`}
                />
              </div>
            </div>

            {/* RIGHT: form card with horizontal slider */}
            <div className="observe-me slide-right">
              <div className="login-card p-8 md:p-10 max-w-md mx-auto lg:ml-auto w-full">
                <div className="form-carousel">
                  <div className={`form-slide ${formMode === 'login' ? 'active' : 'slid-left'}`}>
                    <>
                      <div className="flex items-center gap-3 mb-6">
                        <div className="w-11 h-11 rounded-2xl bg-primary-soft flex items-center justify-center flex-shrink-0">
                          <LogIn size={18} className="text-primary-dark" />
                        </div>
                        <div>
                          <h3 className="font-display text-xl font-bold leading-tight">Iniciar sesión</h3>
                          <p className="text-ink-muted text-xs mt-0.5">Entra con tus credenciales.</p>
                        </div>
                      </div>

                      {loginError && (
                        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm mb-4 flex items-center gap-2">
                          <X size={16} />
                          <span>{loginError}</span>
                        </div>
                      )}

                      <form onSubmit={handleLogin} className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-ink mb-2">Usuario o email</label>
                          <div className="relative">
                            <User size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-muted" />
                            <input
                              type="text"
                              value={loginUsername}
                              onChange={(e) => setLoginUsername(e.target.value)}
                              placeholder="nombre@empresa.com"
                              className="login-form-input"
                              required
                            />
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-ink mb-2">Contraseña</label>
                          <div className="relative">
                            <Lock size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-muted" />
                            <input
                              type="password"
                              value={loginPassword}
                              onChange={(e) => setLoginPassword(e.target.value)}
                              placeholder="••••••••"
                              className="login-form-input"
                              required
                            />
                          </div>
                        </div>

                        <div className="flex items-center justify-between text-sm">
                          <label className="flex items-center gap-2 text-ink-muted cursor-pointer">
                            <input type="checkbox" className="rounded" />
                            Recordarme
                          </label>
                          <button
                            type="button"
                            onClick={() => navigate('/forgot-password')}
                            className="text-primary-dark font-medium hover:underline"
                          >
                            ¿Olvidaste la contraseña?
                          </button>
                        </div>

                        <button type="submit" disabled={loginLoading} className="login-btn">
                          {loginLoading ? (
                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                          ) : (
                            <>Acceder <ArrowRight size={18} /></>
                          )}
                        </button>
                      </form>

                      <div className="form-switch">
                        <span>¿Aún no usáis TemControl?</span>
                        <button
                          onClick={() => setFormMode('access')}
                          className="form-switch-btn"
                        >
                          Solicitar acceso <ArrowRight size={14} />
                        </button>
                      </div>
                    </>
                  </div>

                  <div className={`form-slide ${formMode === 'access' ? 'active' : 'slid-right'}`}>
                    <>
                      <div className="flex items-center gap-3 mb-6">
                        <div className="w-11 h-11 rounded-2xl bg-accent-light flex items-center justify-center flex-shrink-0">
                          <Send size={18} className="text-accent-dark" />
                        </div>
                        <div>
                          <h3 className="font-display text-xl font-bold leading-tight">Solicitar acceso</h3>
                          <p className="text-ink-muted text-xs mt-0.5">Tres datos y te contactamos en 48h.</p>
                        </div>
                      </div>

                      {accessSent ? (
                        <div className="text-center py-6 success-message">
                          <div className="w-16 h-16 rounded-full bg-primary-light flex items-center justify-center mx-auto mb-4">
                            <CheckCircle2 size={32} className="text-primary-dark" />
                          </div>
                          <h4 className="font-display text-xl font-bold mb-2">¡Solicitud enviada!</h4>
                          <p className="text-ink-muted text-sm mb-6 max-w-sm mx-auto">
                            Nuestro equipo revisará tu solicitud y te contactará por email en las próximas 48h.
                          </p>
                          <button
                            onClick={() => setAccessSent(false)}
                            className="text-primary-dark font-semibold text-sm hover:underline"
                          >
                            Enviar otra solicitud
                          </button>
                        </div>
                      ) : (
                        <form onSubmit={handleAccessSubmit} className="space-y-4">
                          <div>
                            <label className="block text-sm font-medium text-ink mb-2">Nombre de la empresa</label>
                            <div className="relative">
                              <Building2 size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-muted" />
                              <input
                                type="text"
                                value={accessForm.empresa}
                                onChange={(e) => setAccessForm({ ...accessForm, empresa: e.target.value })}
                                placeholder="Mi Empresa S.L."
                                className={`login-form-input ${accessErrors.empresa ? 'login-form-input-error' : ''}`}
                              />
                            </div>
                            {accessErrors.empresa && <p className="text-xs text-danger mt-1">{accessErrors.empresa}</p>}
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-ink mb-2">Rango de trabajadores</label>
                            <div className="relative">
                              <Users size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-muted pointer-events-none" />
                              <select
                                value={accessForm.rango}
                                onChange={(e) => setAccessForm({ ...accessForm, rango: e.target.value })}
                                className={`login-form-input appearance-none pr-10 ${accessErrors.rango ? 'login-form-input-error' : ''}`}
                              >
                                <option value="">Selecciona un rango</option>
                                <option value="1-10">1 - 10 empleados</option>
                                <option value="11-50">11 - 50 empleados</option>
                                <option value="51-200">51 - 200 empleados</option>
                                <option value="201-500">201 - 500 empleados</option>
                                <option value="500+">Más de 500 empleados</option>
                              </select>
                              <ChevronDown size={18} className="absolute right-3 top-1/2 -translate-y-1/2 text-ink-muted pointer-events-none" />
                            </div>
                            {accessErrors.rango && <p className="text-xs text-danger mt-1">{accessErrors.rango}</p>}
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-ink mb-2">Email de contacto</label>
                            <div className="relative">
                              <Mail size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-muted" />
                              <input
                                type="email"
                                value={accessForm.email}
                                onChange={(e) => setAccessForm({ ...accessForm, email: e.target.value })}
                                placeholder="nombre@empresa.com"
                                className={`login-form-input ${accessErrors.email ? 'login-form-input-error' : ''}`}
                              />
                            </div>
                            {accessErrors.email && <p className="text-xs text-danger mt-1">{accessErrors.email}</p>}
                          </div>

                          <button type="submit" disabled={accessLoading} className="login-btn">
                            {accessLoading ? (
                              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                            ) : (
                              <>Enviar solicitud <Send size={16} /></>
                            )}
                          </button>

                          <p className="text-xs text-ink-muted text-center">
                            Al enviar aceptas que contactemos contigo por email.
                          </p>
                        </form>
                      )}

                      <div className="form-switch">
                        <button
                          onClick={() => { setFormMode('login'); setAccessSent(false); }}
                          className="form-switch-btn"
                        >
                          <ArrowLeft size={14} /> Volver a iniciar sesión
                        </button>
                      </div>
                    </>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ======= FAQ ======= */}
      <section id="faq" className="section-full bg-white">
        <div className="max-w-7xl mx-auto w-full">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* LEFT: heading + image */}
            <div className="faq-aside observe-me slide-left">
              <span className="eyebrow">Preguntas frecuentes</span>
              <h2 className="text-3xl md:text-4xl font-bold mb-5 text-balance">
                {faqMode === 'faq'
                  ? 'Lo que las empresas nos preguntan.'
                  : '¿Tienes una duda concreta?'}
              </h2>
              <p className="text-lg text-ink-muted leading-relaxed mb-8 text-balance">
                {faqMode === 'faq'
                  ? 'Respondemos a las dudas más habituales. Si te queda alguna, envíanos tu pregunta desde aquí mismo.'
                  : 'Cuéntanos de dónde vienes y qué necesitas saber. Te respondemos en menos de 48h al email de contacto.'}
              </p>

              <div className="faq-visual">
                <img src={IMAGES.faq} alt="" loading="lazy" />
              </div>
            </div>

            {/* RIGHT: carousel (accordion ↔ question form) */}
            <div className="observe-me slide-right">
              <div className="form-carousel">
                {/* FAQ ACCORDION */}
                <div className={`form-slide ${faqMode === 'faq' ? 'active' : 'slid-left'}`}>
                  <div className="space-y-3">
                    {faqs.map((f, i) => (
                      <div key={i} className={`faq-item ${openFaq === i ? 'open' : ''}`}>
                        <button
                          className="faq-question"
                          onClick={() => setOpenFaq(openFaq === i ? null : i)}
                        >
                          <span>{f.q}</span>
                          <ChevronDown size={20} className="faq-chevron" />
                        </button>
                        <div className="faq-answer">
                          <p>{f.a}</p>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="form-switch">
                    <span>¿Tienes más dudas?</span>
                    <button
                      onClick={() => setFaqMode('question')}
                      className="form-switch-btn"
                    >
                      Envíanos tu pregunta <ArrowRight size={14} />
                    </button>
                  </div>
                </div>

                {/* QUESTION FORM */}
                <div className={`form-slide ${faqMode === 'question' ? 'active' : 'slid-right'}`}>
                  <div className="faq-question-form">
                    <div className="flex items-center gap-3 mb-6">
                      <div className="w-11 h-11 rounded-2xl bg-accent-light flex items-center justify-center flex-shrink-0">
                        <MessageSquare size={18} className="text-accent-dark" />
                      </div>
                      <div>
                        <h3 className="font-display text-xl font-bold leading-tight">Envíanos tu pregunta</h3>
                        <p className="text-ink-muted text-xs mt-0.5">Te respondemos en menos de 48h.</p>
                      </div>
                    </div>

                    {questionSent ? (
                      <div className="text-center py-6 success-message">
                        <div className="w-16 h-16 rounded-full bg-primary-light flex items-center justify-center mx-auto mb-4">
                          <CheckCircle2 size={32} className="text-primary-dark" />
                        </div>
                        <h4 className="font-display text-xl font-bold mb-2">¡Pregunta enviada!</h4>
                        <p className="text-ink-muted text-sm mb-6 max-w-sm mx-auto">
                          Nuestro equipo te responderá por email en las próximas 48h.
                        </p>
                        <button
                          onClick={() => setQuestionSent(false)}
                          className="text-primary-dark font-semibold text-sm hover:underline"
                        >
                          Enviar otra pregunta
                        </button>
                      </div>
                    ) : (
                      <form onSubmit={handleQuestionSubmit} className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-ink mb-2">Nombre de la empresa</label>
                          <div className="relative">
                            <Building2 size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-muted" />
                            <input
                              type="text"
                              value={questionForm.empresa}
                              onChange={(e) => setQuestionForm({ ...questionForm, empresa: e.target.value })}
                              placeholder="Mi Empresa S.L."
                              className={`login-form-input ${questionErrors.empresa ? 'login-form-input-error' : ''}`}
                            />
                          </div>
                          {questionErrors.empresa && <p className="text-xs text-danger mt-1">{questionErrors.empresa}</p>}
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-ink mb-2">¿Ya sois clientes de TemControl?</label>
                          <div className="relative">
                            <HelpCircle size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-muted pointer-events-none" />
                            <select
                              value={questionForm.cliente}
                              onChange={(e) => setQuestionForm({ ...questionForm, cliente: e.target.value })}
                              className={`login-form-input appearance-none pr-10 ${questionErrors.cliente ? 'login-form-input-error' : ''}`}
                            >
                              <option value="">Selecciona una opción</option>
                              <option value="si">Sí, ya somos clientes</option>
                              <option value="no">No, estamos valorándolo</option>
                              <option value="explorando">Solo estamos explorando</option>
                            </select>
                            <ChevronDown size={18} className="absolute right-3 top-1/2 -translate-y-1/2 text-ink-muted pointer-events-none" />
                          </div>
                          {questionErrors.cliente && <p className="text-xs text-danger mt-1">{questionErrors.cliente}</p>}
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-ink mb-2">Tu pregunta</label>
                          <textarea
                            value={questionForm.pregunta}
                            onChange={(e) => setQuestionForm({ ...questionForm, pregunta: e.target.value })}
                            placeholder="Escribe aquí tu duda con el máximo detalle posible..."
                            rows={4}
                            className={`login-form-input login-form-textarea ${questionErrors.pregunta ? 'login-form-input-error' : ''}`}
                          />
                          {questionErrors.pregunta && <p className="text-xs text-danger mt-1">{questionErrors.pregunta}</p>}
                        </div>

                        <button type="submit" disabled={questionLoading} className="login-btn">
                          {questionLoading ? (
                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                          ) : (
                            <>Enviar pregunta <Send size={16} /></>
                          )}
                        </button>

                        <p className="text-xs text-ink-muted text-center">
                          Recibirás la respuesta en el email asociado a tu empresa.
                        </p>
                      </form>
                    )}

                    <div className="form-switch">
                      <button
                        onClick={() => { setFaqMode('faq'); setQuestionSent(false); }}
                        className="form-switch-btn"
                      >
                        <ArrowLeft size={14} /> Volver a las preguntas
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ======= FOOTER ======= */}
      <footer className="footer">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-10 mb-10">
            <div>
              <div className="flex items-center gap-2.5 mb-4">
                <Logo size={36} variant="onDark" />
                <span className="font-display font-bold text-xl text-white">TemControl</span>
              </div>
              <p className="text-sm text-slate-400 leading-relaxed">
                Plataforma de gestión horaria para empresas modernas. Fichajes, vacaciones y calendarios en un solo sitio.
              </p>
            </div>

            <div>
              <h4>Producto</h4>
              <a href="#funciones" className="footer-link">Funciones</a>
              <a href="#proceso" className="footer-link">Cómo funciona</a>
              <a href="#calendario" className="footer-link">Calendario</a>
              <a href="#login" className="footer-link">Acceder</a>
            </div>

            <div>
              <h4>Empresa</h4>
              <a href="#" className="footer-link">Sobre nosotros</a>
              <a href="#" className="footer-link">Clientes</a>
              <a href="#" className="footer-link">Blog</a>
              <a href="#" className="footer-link">Contacto</a>
            </div>

            <div>
              <h4>Contacto</h4>
              <a
                href="mailto:administracion@holainformatica.com"
                className="flex items-center gap-2 text-sm text-slate-400 hover:text-white mb-2 transition-colors"
              >
                <Mail size={14} /> administracion@holainformatica.com
              </a>
              <a
                href="tel:+34670595476"
                className="flex items-center gap-2 text-sm text-slate-400 hover:text-white mb-2 transition-colors"
              >
                <Phone size={14} /> +34 670 595 476
              </a>
              <div className="flex items-center gap-2 text-sm text-slate-400">
                <MapPin size={14} /> Sevilla, España
              </div>
            </div>
          </div>

          <div className="footer-bottom flex flex-col md:flex-row items-center justify-between gap-3">
            <span>© {new Date().getFullYear()} TemControl. Todos los derechos reservados.</span>
            <div className="flex gap-5">
              <a href="#" className="footer-link">Privacidad</a>
              <a href="#" className="footer-link">Términos</a>
              <a href="#" className="footer-link">Cookies</a>
            </div>
          </div>
        </div>
      </footer>

    </div>
  );
};

export default HomePage;
